import usePageMeta, { breadcrumbGraph } from '../hooks/usePageMeta';
import LegalPage from './LegalPage';

const UPDATED = 'Last updated September 29, 2026';
const EMAIL = 'printablegraphpaper@gmail.com';
const GA_POLICY = 'https://policies.google.com/privacy';
const GA_OPTOUT = 'https://tools.google.com/dlpage/gaoptout';
const PRIVACY_URL = 'https://printablegraphpaperpdf.com/privacy';

const PRIVACY_JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      name: 'Privacy Policy | Printable Graph Paper',
      url: PRIVACY_URL,
      inLanguage: 'en',
      description:
        'How Printable Graph Paper handles information — no accounts, in-browser generation, Google Analytics details, cookies, and the choices you have.',
      isPartOf: { '@id': 'https://printablegraphpaperpdf.com/' },
    },
    breadcrumbGraph('Privacy Policy', PRIVACY_URL),
  ],
};

const SECTIONS = [
  {
    id: 'introduction',
    title: 'Introduction',
    body: (
      <>
        <p>
          This Privacy Policy explains how Printable Graph Paper (&quot;we&quot;, &quot;us&quot;)
          handles information when you visit printablegraphpaperpdf.com — and, just as importantly,
          what we do not collect.
        </p>
        <p>The short version: there are no accounts, nothing you generate is uploaded, and your settings stay in your browser.</p>
      </>
    ),
  },
  {
    id: 'information',
    title: 'Information We Collect',
    body: (
      <>
        <p>We do not ask for personal information, and there is no way to create an account on this site. The limited information involved in using the site is:</p>
        <ul>
          <li>
            <strong>Google Analytics</strong> — aggregate usage data such as pages viewed, a
            general region derived from your IP address, device and browser type, and referral
            source. Details in section 4.
          </li>
          <li>
            <strong>Theme preference</strong> — your light or dark mode choice is stored in your
            browser&apos;s local storage under the key <strong>graph-paper-theme</strong>. It
            never leaves your device.
          </li>
          <li>
            <strong>Generator settings</strong> — the grid you configure is processed entirely in
            your browser. No sheet, image, or setting is sent to us.
          </li>
          <li>
            <strong>Contact form</strong> — this site has no form server. Nothing you type into the
            form is transmitted or stored by the website; it only becomes an email when you choose
            to send it from your own email app.
          </li>
        </ul>
        <div className="page-note">
          <span className="page-note-label">We do not collect</span>
          <p>
            Names, account details, passwords, payment information, precise location, personal
            profiles, or the contents of your generated sheets.
          </p>
        </div>
      </>
    ),
  },
  {
    id: 'usage',
    title: 'How We Use Information',
    body: (
      <>
        <p>We use the limited information above only to:</p>
        <ul>
          <li>operate and improve the website — for example, seeing which pages and paper types are useful;</li>
          <li>remember your theme preference between visits;</li>
          <li>diagnose problems and understand which browsers and devices to support.</li>
        </ul>
        <p>
          We do not sell, rent, or trade information, and we do not use it to build advertising
          profiles.
        </p>
      </>
    ),
  },
  {
    id: 'analytics',
    title: 'Analytics',
    body: (
      <>
        <p>
          The site uses Google Analytics 4 through the Google tag (gtag.js), with measurement ID{' '}
          <strong>G-L70TPG77Q9</strong>. Google Analytics uses cookies and processes data —
          including your IP address — on our behalf according to{' '}
          <a href={GA_POLICY} target="_blank" rel="noreferrer noopener">
            Google&apos;s privacy policy
          </a>
          .
        </p>
        <p>
          We only look at aggregated reports, such as which pages are visited most. We do not
          receive data that identifies you individually, and we do not combine Analytics data with
          personal data we hold.
        </p>
        <p>
          You can opt out of Google Analytics entirely with{' '}
          <a href={GA_OPTOUT} target="_blank" rel="noreferrer noopener">
            Google&apos;s browser add-on
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies and Similar Technologies',
    body: (
      <>
        <p>
          We do not set our own advertising or tracking cookies. Google Analytics may set cookies
          such as <strong>_ga</strong> and <strong>_ga_*</strong> to distinguish users and remember
          information about a visit.
        </p>
        <p>
          We also use your browser&apos;s local storage — not a cookie — to remember your theme.
          You can clear it at any time in your browser settings; doing so simply resets your theme
          choice.
        </p>
      </>
    ),
  },
  {
    id: 'advertising',
    title: 'Advertising and Third-Party Services',
    body: (
      <>
        <p>
          We do not run advertising on this website. No advertising network — including Google
          AdSense — is integrated, and no third-party ads are displayed.
        </p>
        <p>The third-party services that actually run on the site are:</p>
        <ul>
          <li>
            <strong>Google Analytics</strong> (measurement) — see section 4.
          </li>
          <li>
            <strong>Google Fonts</strong> (fonts.googleapis.com and fonts.gstatic.com) — serves the
            Inter typeface used on the site; Google receives your IP address when your browser
            requests the font.
          </li>
        </ul>
        <div className="page-note">
          <span className="page-note-label">If that changes</span>
          <p>
            If we ever add advertising or new third parties, we will update this section and, where
            the law requires it, ask for your consent first.
          </p>
        </div>
      </>
    ),
  },
  {
    id: 'retention',
    title: 'Data Retention',
    body: (
      <>
        <p>
          We do not operate a database of personal data — there is nothing stored on our side for
          us to retain. Analytics data is retained by Google according to the retention settings of
          our Analytics property and Google&apos;s defaults, then deleted or aggregated.
        </p>
        <p>
          Data stored in your browser (your theme preference) stays there until you clear your
          browser data or we change the storage key.
        </p>
      </>
    ),
  },
  {
    id: 'third-party-links',
    title: 'Third-Party Links',
    body: (
      <p>
        The site links to other pages — including our own generators and occasional external
        resources such as Google&apos;s policy pages. Once you leave this site, the other
        party&apos;s privacy policy applies, and we are not responsible for it.
      </p>
    ),
  },
  {
    id: 'children',
    title: "Children's Privacy",
    body: (
      <p>
        The website is not directed to children under 13, and we do not knowingly collect personal
        information from them. If you believe a child has provided personal information through any
        channel, contact us and we will remove it.
      </p>
    ),
  },
  {
    id: 'choices',
    title: 'Your Choices',
    body: (
      <>
        <p>You control most of what happens on your device:</p>
        <ul>
          <li>Block or delete cookies in your browser settings — this also disables Google Analytics for this site.</li>
          <li>
            Install{' '}
            <a href={GA_OPTOUT} target="_blank" rel="noreferrer noopener">
              Google&apos;s Analytics opt-out add-on
            </a>
            .
          </li>
          <li>Use private or incognito browsing so cookies and local storage are not kept.</li>
          <li>Clear this site&apos;s data to reset your saved theme preference.</li>
        </ul>
        <p>
          Because the generator has no accounts, there is no profile to review, correct, or delete —
          and nothing stored on our side to erase.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to This Privacy Policy',
    body: (
      <p>
        We may revise this policy from time to time. The &quot;Last updated&quot; date at the top
        of this page shows when it was last changed, so check back periodically.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    body: (
      <p>
        Questions about this Privacy Policy or your information? Email{' '}
        <a href={'mailto:' + EMAIL}>{EMAIL}</a> or use the <a href="/contact">contact page</a>.
      </p>
    ),
  },
];

function Privacy() {
  usePageMeta({
    title: 'Privacy Policy | Printable Graph Paper',
    description:
      'How Printable Graph Paper handles information — no accounts, in-browser generation, Google Analytics details, cookies, and the choices you have.',
    keywords: 'privacy, privacy policy, printable graph paper, data, cookies, google analytics',
    canonical: 'https://printablegraphpaperpdf.com/privacy',
    ogTitle: 'Privacy Policy | Printable Graph Paper',
    ogDescription:
      'What information Printable Graph Paper collects — and what it does not — plus how that information is used.',
    ogUrl: 'https://printablegraphpaperpdf.com/privacy',
    jsonLd: PRIVACY_JSONLD,
  });

  return (
    <LegalPage
      eyebrow="Privacy"
      title="Your privacy matters."
      leads={[
        'This page explains what information Printable Graph Paper collects — and, just as importantly, what it does not — plus how that information is used and the choices you have.',
      ]}
      meta={
        <>
          <span className="page-chip">
            <span className="page-chip-dot" />
            {UPDATED}
          </span>
          <span className="page-chip">No accounts, no ad tracking</span>
        </>
      }
      sections={SECTIONS}
      cta={{
        eyebrow: 'Questions',
        title: 'Questions about your privacy?',
        body: 'Ask us anything about what we collect — or what we do not.',
        primary: { label: 'Contact us', href: '/contact' },
        secondary: { label: 'Terms and conditions', href: '/terms' },
      }}
    />
  );
}

export default Privacy;
